import { FilterAndSortOptions, multiTypeSort } from 'src/state/redux/selector/common';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { API, graphqlOperation } from '@aws-amplify/api'
import { FullTextSearchQuery, TextWithHighlights } from '@alucio/aws-beacon-amplify/src/API'
import {
  fullTextSearch as fullTextSearchQuery,
} from '@alucio/aws-beacon-amplify/src/graphql/queries'
import { DocumentORM } from 'src/types/orms'
import {
  DocumentSearchOptions,
  useCanPerformSearch,
  useDocumentSearch,
} from 'src/state/redux/selector/documentSearch/documentSearch';
import { useAppSettings } from 'src/state/context/AppSettings'
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import useCurrentPage from 'src/components/DNA/hooks/useCurrentPage';
import { merge } from 'src/state/redux/document/query'
import debounce from 'lodash/debounce'
import { useAllDocumentsInstance } from 'src/state/redux/selector/document';
import useIsDebugView from 'src/hooks/useIsDebugView/useIsDebugView';
type SearchStatus = 'IDLE' | 'PENDING' | 'RESULTS' | 'EMPTY_RESULTS' | 'FALLBACK';
type Mode =
  'HEADER' | 'VIEWER_RESULTS' | 'PUBLISHER_RESULTS' | 'FOLDER' | 'CUSTOM' | 'MEETING'

export type SearchContextValue = {
  query: FilterAndSortOptions<DocumentORM> | null | undefined
  documentIdSnippetMap: Map<string, documentSnippetType>
  fullTextSearch: ReturnType<typeof debounce<(searchText: string) => Promise<void>>>;
  searchText: string;
  status: SearchStatus
  resetSearch: () => void;
  mode?: Mode
  isLoadingSearch: boolean;
  searchTiming: {
    serverTime: number | null | undefined;
    clientTime: number | null | undefined;
    totalDuration: number | null | undefined;
    processedQuery: string | null | undefined;
    errorMessage: string | null;
  };
}

const SearchContext = createContext<SearchContextValue>({
  query: null,
  documentIdSnippetMap: new Map(),
  fullTextSearch: debounce(async (_: string) => undefined, 500),
  searchText: '',
  status: 'IDLE',
  resetSearch: () => undefined,
  isLoadingSearch: false,
  searchTiming: {
    serverTime: null,
    clientTime: null,
    totalDuration: null,
    processedQuery: null,
    errorMessage: null,
  },
})

interface ProviderProps {
  mode: Mode
  searchText?: string,
  children: React.ReactElement
}

export type documentSnippetType = {
  title: TextWithHighlights,
  documentExcerpt: TextWithHighlights
  resultId: string
  queryId: string
  index: number
  pageNumber: number
}

const constructQuery = (idMap: Map<string, documentSnippetType>) => {
  const newFilterAndSortOptions: FilterAndSortOptions<DocumentORM> = {
    filter: {
      model: {
        id: (id) => idMap.has(id),
      },
    },
    sort: [
      {
        model: {
          id: (a, b) => {
            return multiTypeSort(idMap.get(a)?.index, idMap.get(b)?.index, { dir: 'asc' })
          },
        },
      },
    ],
  }
  return newFilterAndSortOptions
}

const currentPageQuery = { exact: false }
// todo: flexible filtering
const statusFilters = {
  PUBLISHER: ['PUBLISHED', 'NOT_PUBLISHED', 'ARCHIVED', 'REVOKED'],
  VIEWER: ['PUBLISHED'],
}

const recordSearchEvent = (mode: Mode) => {
  // adding this check since we don't want to record events as they type in the search dropdown
  // only when they hit enter
  if (mode !== 'HEADER') {
    analytics?.track('SEARCH_DOCUMENT_SEARCH', {
      category: 'SEARCH',
      action: 'DOCUMENT_SEARCH',
      context: mode,
    });
  }
}

/**
 * Tracks document search analytics.
 *
 * This function sends analytics data related to document searches. It includes
 * both server and client search times, the duration of the search, the mode of
 * the search, and the search text itself.
 *
 * @param {number | null | undefined} searchServerTime - "took" attribute from Open Search result, in JSON number format.
 * @param {number | null | undefined} searchClientTime - Time for end to end response time openSearch, in JSON number format.
 * @param {number} duration - Total end to end time measured by the client.
 * @param {Mode} mode - The mode of the search.
 * @param {string} searchText - The text used for the search.
 */
const trackDocumentSearchAnalytics = (
  searchServerTime: number | null | undefined,
  searchClientTime: number | null | undefined,
  duration: number,
  searchText: string,
) => {
  try {
    const totalTime = duration;
    analytics?.track('SEARCH_REQUEST', {
      category: 'SEARCH',
      action: 'REQUEST',
      searchServerTime: searchServerTime,
      searchClientTime: searchClientTime,
      totalTime,
      searchText,
    });
  } catch (error) {
    console.error('Failed to track document search analytics:', error);
  }
}

const useNameOfSearchAdapter = () => {
  const enableHybridSearch_V2_0 = useFeatureFlags('enableHybridSearch_V2_0');
  const enableHybridSearch_V2_1 = useFeatureFlags('enableHybridSearch_V2_1');

  // If V2.1 flag is enabled, it takes precedence
  if (enableHybridSearch_V2_1) {
    return 'Hybrid_Search_v2_1';
  }

  // If V2.0 flag is enabled
  if (enableHybridSearch_V2_0) {
    return 'Hybrid_Search_v2_0';
  }

  // Default case: when both flags are false or undefined
  return 'Hybrid_Search_v1';
}

const DocumentSearchProvider: React.FC<ProviderProps> = ({ mode, children }) => {
  const [query, setQuery] = useState<FilterAndSortOptions<DocumentORM> | null>(null);
  const [lunrText, setLunrText] = useState<string>('');
  const [documentIdSnippetMap, setDocumentIdSnippetMap] =
    useState<Map<string, documentSnippetType>>(new Map());
  const [localText, setLocalText] = useState('');
  const { isOnline } = useAppSettings();
  const canPerformSearch = useCanPerformSearch()
  const searchAdapterName = useNameOfSearchAdapter();
  const route = useCurrentPage(currentPageQuery)
  const [status, setStatus] = useState<SearchStatus>('IDLE')
  const [searchTiming, setSearchTiming] = useState<{
    serverTime: number | null | undefined;
    clientTime: number | null | undefined;
    totalDuration: number | null | undefined;
    processedQuery: string | null | undefined;
    errorMessage: string | null;
  }>({
    serverTime: null,
    clientTime: null,
    totalDuration: null,
    processedQuery: null,
    errorMessage: null,
  });

  const isPublisherRoute = route?.configOptions?.modules?.includes('publisher')
  const isDebugRoute = useIsDebugView()

  const memoSearchOpts: DocumentSearchOptions = useMemo(() => {
    return {
      text: lunrText,
      isPublisher: isPublisherRoute,
    }
  }, [lunrText, isPublisherRoute])

  const lunrResults = useDocumentSearch(memoSearchOpts);

  // Token used to handle "cancellation" of outdated search requests
  // Since Amplify's API.graphql doesn't provide a way to cancel in-flight requests,
  // we use this token to ignore results from outdated searches
  // TODO: Replace this pattern with proper request cancellation once Amplify supports it
  // See: https://github.com/aws-amplify/amplify-js/issues/989
  const searchTokenRef = React.useRef<number>(0);

  const fullTextSearch = useCallback(
    async (searchText: string) => {
      setLocalText(searchText);
      if (isOnline && canPerformSearch(searchText)) {
        setStatus('PENDING')
        recordSearchEvent(mode);

        // Increment token for new search - higher numbers represent more recent searches
        // This ensures only the latest search results are processed
        const currentToken = ++searchTokenRef.current;
        const name = localStorage.getItem('overrideLexicalAdapterName') || searchAdapterName

        try {
          const searchExpression = fullTextSearchQuery;

          const startTime = performance.now();
          const { data } = await API.graphql(graphqlOperation(searchExpression, {
            queryText: searchText,
            filter: {
              statuses: statusFilters[(isPublisherRoute || isDebugRoute) ? 'PUBLISHER' : 'VIEWER'],
            },
            name,
          })) as { data: FullTextSearchQuery };

          // If this search is no longer the most recent one, discard its results
          // This prevents race conditions where older search results could override newer ones
          if (currentToken !== searchTokenRef.current) {
            return;
          }

          const endTime = performance.now();
          const duration = (endTime - startTime) / 1000; // Convert milliseconds to seconds

          const documentResultMap: Map<string, documentSnippetType> = new Map();
          const dataResult = (data as FullTextSearchQuery).fullTextSearch;

          // Update search timing information
          setSearchTiming({
            serverTime: dataResult?.searchServerTime,
            clientTime: dataResult?.searchClientTime,
            totalDuration: duration,
            processedQuery: dataResult?.processedQuery,
            errorMessage: null,
          });

          dataResult?.items?.forEach((curr, index) => {
            if (curr?.documentId) {
              const documentId = curr.documentId
              documentResultMap.set(documentId, {
                title: curr.title,
                documentExcerpt: curr.documentExcerpt,
                resultId: curr.resultId,
                queryId: curr.queryId,
                pageNumber: curr.pageNumber || 1,
                index: index,
              });
            }
          });

          // fallback to lunr
          if (documentResultMap.size === 0) {
            setLunrText(searchText);
            setStatus('FALLBACK');
          }
          else {
            setDocumentIdSnippetMap(documentResultMap);
            setQuery(constructQuery(documentResultMap));
            setStatus('RESULTS');

            trackDocumentSearchAnalytics(
              dataResult?.searchServerTime,
              dataResult?.searchClientTime,
              duration,
              searchText,
            );
          }
        }
        catch (err) {
          // fallback to lunr
          if (currentToken === searchTokenRef.current) {
            const errorMessage = err instanceof Error
              ? err.message
              : (err as any)?.errors?.[0]?.message || 'Unknown error occurred';
            setSearchTiming(prev => ({
              ...prev,
              errorMessage,
            }));
            setLunrText(searchText);
            setStatus('FALLBACK');
          }
        }
      }
      else if (canPerformSearch(searchText)) {
        recordSearchEvent(mode);
        setLunrText(searchText);
        setStatus('FALLBACK');
      }
    },
    [isOnline, isPublisherRoute, searchAdapterName],
  );

  const debouncedSearch = useCallback(
    debounce(fullTextSearch, 500),
    [fullTextSearch],
  );

  useEffect(
    () => {
      if (status === 'FALLBACK') {
        const map = lunrResults.reduce((acc, curr) => {
          acc.set(curr.model.id, undefined);
          return acc;
        }, new Map());
        setDocumentIdSnippetMap(map);
        setQuery(constructQuery(map));

        if (map.size === 0) {
          analytics?.track('NO_RESULT', {
            category: 'SEARCH',
            action: 'NO_RESULT',
          });
          setStatus('EMPTY_RESULTS');
        }
        else {
          setStatus('RESULTS');
        }
      }
    },
    [lunrResults, status],
  )

  const resetSearch = () => {
    setQuery(null);
    setDocumentIdSnippetMap(new Map());
    setLunrText('');
    setLocalText('');
    setStatus('IDLE');
  }

  return (
    <SearchContext.Provider
      value={{
        query,
        documentIdSnippetMap,
        fullTextSearch: debouncedSearch,
        searchText: localText,
        status,
        mode,
        resetSearch,
        isLoadingSearch: !(status === 'EMPTY_RESULTS' || status === 'RESULTS'),
        searchTiming,
      }}
    >
      {children}
    </SearchContext.Provider>
  )
}

export const useDocumentSearchV2Context = () => useContext(SearchContext)

export const documentSearchV2Factory = (contextIn: typeof SearchContext) => (
  searchText?: string,
  ...queriesIn: FilterAndSortOptions<DocumentORM>[]
) => {
  const context = useContext(contextIn);

  useEffect(
    () => {
      if (searchText) {
        context.fullTextSearch(searchText)
      }
    },
    [searchText],
  )

  const searchQuery: FilterAndSortOptions<DocumentORM> | undefined = useMemo(
    () => merge(context.query, ...queriesIn),
    [context.query, ...queriesIn],
  )

  // [TODO-4140] - Possibly make this optional somehow or return an already memoized value if the consumer doesn't care about these results
  const docORMResults = useAllDocumentsInstance(searchQuery)

  const value = useMemo(
    () => ({
      ...context,
      searchQuery,
      documentORMSearchResults: docORMResults,
    }),
    [context, searchQuery, docORMResults],
  )

  return value
}

export const useDocumentSearchV2 = documentSearchV2Factory(SearchContext)

export default DocumentSearchProvider;
